import Dexie, { type Table } from 'dexie';
import type { WorkOrder } from '../orders/types';
import type { VisitSession, ChecklistItemResult, PhotoRecord, SignatureRecord, ChecklistPhotoRecord } from '../visits/types';
import type { OutboxItem } from '../sync/types';
import type { ChecklistTemplate } from '../templates/types';
import type { Customer } from '../customers/types';
import type { CustomerObject, ObjectType } from '../objects/types';
import type { Invoice } from '../invoices/types';
import type { AuditLogEntry } from '../audit_log/types';
import type { CompanyAcquiringSettingsPublic } from '../acquiring/types';
import { getPilotChecklistTemplates } from '../../shared/constants';

/**
 * 1.18.1 — Запись sync_meta хранит cursor последней успешной
 * incremental-синхронизации для одной сущности.
 *
 *   key — составной ключ "{entity}:{company_id}", например
 *         "customers:550e8400-...". Это позволяет одному устройству
 *         использоваться разными аккаунтами без перекрёстного
 *         мусора (хотя в Кворум это редкий кейс).
 *   entity — имя домена для категоризации/отладки.
 *   last_sync_at — ISO timestamp последней успешной выгрузки. Следующий
 *         pull дёргает `where updated_at > last_sync_at`.
 *   updated_at — когда мы обновили эту мета-запись.
 */
export interface SyncMetaRecord {
  key: string;
  entity: 'customers' | 'customer_objects' | 'object_types' | 'invoices' | 'orders' | 'audit_log';
  company_id: string;
  last_sync_at: string;
  updated_at: string;
}

/**
 * 1.19.8 — Кэш реквизитов компании в Dexie.
 *
 * Один rec на company_id. Хранит снимок PublicCompanyInfo (то что
 * fetchPublicCompanyInfo читает с сервера): название, логотип, телефон,
 * email и payment_details (банк, счёт, БИК, ИНН, КПП, телефон СБП).
 *
 * Назначение — позволить PaymentScreen работать в офлайне. Технику
 * нужен номер СБП и реквизиты для перевода клиенту, даже когда нет
 * сети. Раньше fetchPublicCompanyInfo возвращал null при !navigator.onLine,
 * и UI показывал «Номер телефона для СБП не указан».
 *
 * Заполнение: при логине и периодически (через hydrateCompanyInfo,
 * exposed в shared/api).
 */
export interface CompanyInfoRecord {
  company_id: string;
  name: string;
  logo_url: string;
  phone: string;
  email: string;
  payment_details: {
    bank_name: string;
    account: string;
    bik: string;
    inn: string;
    kpp: string;
    sbp_phone: string;
  };
  updated_at: string;
}

export class KvorumDB extends Dexie {
  orders!: Table<WorkOrder>;
  visit_sessions!: Table<VisitSession>;
  checklist_results!: Table<ChecklistItemResult>;
  photos!: Table<PhotoRecord>;
  signatures!: Table<SignatureRecord>;
  outbox!: Table<OutboxItem>;
  templates!: Table<ChecklistTemplate>;
  // 1.15.0 — Фото ДО/ПОСЛЕ, привязанные к пункту чек-листа.
  checklist_photos!: Table<ChecklistPhotoRecord>;
  // 1.17.0a — offline-first: customers, customer_objects, object_types, invoices.
  customers!: Table<Customer>;
  customer_objects!: Table<CustomerObject>;
  object_types!: Table<ObjectType>;
  invoices!: Table<Invoice>;
  // 1.18.1 — Cursor для incremental sync по каждой сущности.
  sync_meta!: Table<SyncMetaRecord>;
  // 1.19.0 — Audit log offline. Локальный кэш событий + outbox для записи.
  audit_log!: Table<AuditLogEntry>;
  // 1.19.8 — Кэш реквизитов компании для офлайн-оплаты.
  company_info!: Table<CompanyInfoRecord>;
  // 1.27.0 — Настройки эквайринга компании (без секретов, только публичные поля).
  // Один rec на company_id. Заполняется через refreshAcquiringSettingsFromServer.
  // Используется PaymentScreen для решения «показывать ли кнопку онлайн-оплаты».
  acquiring_settings!: Table<CompanyAcquiringSettingsPublic>;

  constructor() {
    super('fieldsnap_v1');

    this.version(1).stores({
      orders: 'id, status, assigned_to, scheduled_date, [assigned_to+scheduled_date]',
      visit_sessions: 'order_id',
      checklist_results: 'id, order_id',
      photos: 'id, order_id, upload_status',
      signatures: 'order_id, upload_status',
      outbox: 'id, status, created_at, order_id',
      templates: 'id',
    });

    this.version(2).stores({
      orders: 'id, status, assigned_to, scheduled_date, updated_at, [assigned_to+scheduled_date]',
      visit_sessions: 'order_id, current_screen, started_at, completed_at',
      checklist_results: 'id, order_id, item_id, status',
      photos: 'id, order_id, upload_status, taken_at',
      signatures: 'order_id, upload_status, signed_at',
      outbox: 'id, status, created_at, order_id, next_retry_at, locked_at',
      templates: 'id, work_type, is_active',
    });

    this.version(3).stores({
      orders: 'id, status, assigned_to, scheduled_date, updated_at, [assigned_to+scheduled_date]',
      visit_sessions: 'order_id, company_id, current_screen, started_at, completed_at',
      checklist_results: 'id, order_id, item_id, status',
      photos: 'id, order_id, upload_status, taken_at',
      signatures: 'order_id, upload_status, signed_at',
      outbox: 'id, status, created_at, order_id, next_retry_at, locked_at',
      templates: 'id, work_type, is_active',
    });

    // Version 4 — adds repeat_of index on orders to support linked-order lookup.
    // repeat_of is nullable; Dexie sparse indexes handle null/undefined gracefully
    // (null entries are simply omitted from the index, no migration logic needed).
    this.version(4).stores({
      orders: 'id, status, assigned_to, scheduled_date, updated_at, repeat_of, [assigned_to+scheduled_date]',
      visit_sessions: 'order_id, company_id, current_screen, started_at, completed_at',
      checklist_results: 'id, order_id, item_id, status',
      photos: 'id, order_id, upload_status, taken_at',
      signatures: 'order_id, upload_status, signed_at',
      outbox: 'id, status, created_at, order_id, next_retry_at, locked_at',
      templates: 'id, work_type, is_active',
    });

    // Version 5 (1.15.0) — adds checklist_photos table for ДО/ПОСЛЕ photos
    // tied to specific checklist items. Indexes:
    //   - order_id          — get all photos for an order (report rendering)
    //   - [order_id+item_id] — get photos for one item (checklist UI)
    //   - upload_status     — outbox processor finds pending uploads
    this.version(5).stores({
      orders: 'id, status, assigned_to, scheduled_date, updated_at, repeat_of, [assigned_to+scheduled_date]',
      visit_sessions: 'order_id, company_id, current_screen, started_at, completed_at',
      checklist_results: 'id, order_id, item_id, status',
      photos: 'id, order_id, upload_status, taken_at',
      signatures: 'order_id, upload_status, signed_at',
      outbox: 'id, status, created_at, order_id, next_retry_at, locked_at',
      templates: 'id, work_type, is_active',
      checklist_photos: 'id, order_id, [order_id+item_id], upload_status, taken_at',
    });

    // Version 6 (1.17.0a) — offline-first для бизнес-сущностей.
    // Добавлены таблицы customers, customer_objects, object_types, invoices.
    //
    // Стратегия id: при создании оффлайн используем crypto.randomUUID(),
    // тот же id отправляется в Supabase когда сеть появится. Если id
    // конфликтует с существующим (крайне маловероятно для UUIDv4) — outbox-
    // обработчик сделает upsert по id и оставит более свежий updated_at.
    //
    // Индексы:
    //   • customers: company_id (RLS-like filtering на клиенте) + phone
    //     (поиск в CustomerSearchField) + name.
    //   • customer_objects: company_id + customer_id (история объектов
    //     клиента) + is_active.
    //   • object_types: company_id + is_active.
    //   • invoices: company_id + order_id (получаем счёт по заявке) + status.
    this.version(6).stores({
      orders: 'id, status, assigned_to, scheduled_date, updated_at, repeat_of, [assigned_to+scheduled_date]',
      visit_sessions: 'order_id, company_id, current_screen, started_at, completed_at',
      checklist_results: 'id, order_id, item_id, status',
      photos: 'id, order_id, upload_status, taken_at',
      signatures: 'order_id, upload_status, signed_at',
      outbox: 'id, status, created_at, order_id, next_retry_at, locked_at',
      templates: 'id, work_type, is_active',
      checklist_photos: 'id, order_id, [order_id+item_id], upload_status, taken_at',
      customers: 'id, company_id, phone, name, updated_at',
      customer_objects: 'id, company_id, customer_id, is_active, updated_at',
      object_types: 'id, company_id, is_active',
      invoices: 'id, company_id, order_id, status, updated_at',
    });

    // Version 7 (1.18.1) — incremental sync cursors.
    //
    // sync_meta хранит ISO-timestamp последней успешной incremental-
    // синхронизации для пары (entity, company_id). При следующем pull
    // дёргается ровно `where updated_at > last_sync_at`. Это резко
    // сокращает трафик при login — особенно критично для пилотов на
    // мобильном интернете.
    //
    // Первый pull (когда sync_meta пуст) — full refresh без cursor.
    // Записи сразу пишутся в bulkPut, last_sync_at обновляется на
    // максимум updated_at из загруженной пачки + safety overlap.
    this.version(7).stores({
      orders: 'id, status, assigned_to, scheduled_date, updated_at, repeat_of, [assigned_to+scheduled_date]',
      visit_sessions: 'order_id, company_id, current_screen, started_at, completed_at',
      checklist_results: 'id, order_id, item_id, status',
      photos: 'id, order_id, upload_status, taken_at',
      signatures: 'order_id, upload_status, signed_at',
      outbox: 'id, status, created_at, order_id, next_retry_at, locked_at',
      templates: 'id, work_type, is_active',
      checklist_photos: 'id, order_id, [order_id+item_id], upload_status, taken_at',
      customers: 'id, company_id, phone, name, updated_at',
      customer_objects: 'id, company_id, customer_id, is_active, updated_at',
      object_types: 'id, company_id, is_active',
      invoices: 'id, company_id, order_id, status, updated_at',
      sync_meta: 'key, entity, company_id',
    });

    // Version 8 (1.18.3) — Compound indexes для быстрого поиска при
    // больших базах (от 1000+ клиентов / 10000+ объектов).
    //
    // До v8:
    //   db.customers.where('company_id').equals(X).toArray()
    //   → линейный фильтр в JS по name/phone substring.
    //   Для 50000 клиентов это десятки мс лагов при наборе в input.
    //
    // С v8:
    //   db.customers.where('[company_id+phone]').between([X, ''], [X, '\uffff'])
    //   → range-запрос по индексу. Префиксный поиск O(log n).
    //   Substring fallback остаётся в JS, но на отфильтрованном tenant
    //   subset.
    //
    // Compound order имеет значение: ставим company_id первым, чтобы
    // одним запросом отсечь tenant, потом по второму ключу.
    //
    // orders.customer_id — добавляем индекс чтобы fetchCustomerOrderHistory
    // не сканировал всю таблицу заявок.
    this.version(8).stores({
      orders: 'id, status, assigned_to, customer_id, scheduled_date, updated_at, repeat_of, [assigned_to+scheduled_date]',
      visit_sessions: 'order_id, company_id, current_screen, started_at, completed_at',
      checklist_results: 'id, order_id, item_id, status',
      photos: 'id, order_id, upload_status, taken_at',
      signatures: 'order_id, upload_status, signed_at',
      outbox: 'id, status, created_at, order_id, next_retry_at, locked_at',
      templates: 'id, work_type, is_active',
      checklist_photos: 'id, order_id, [order_id+item_id], upload_status, taken_at',
      customers: 'id, company_id, phone, name, updated_at, [company_id+phone], [company_id+name]',
      customer_objects: 'id, company_id, customer_id, is_active, updated_at, [company_id+is_active], [customer_id+is_active]',
      object_types: 'id, company_id, is_active, [company_id+is_active]',
      invoices: 'id, company_id, order_id, status, updated_at',
      sync_meta: 'key, entity, company_id',
    });

    // Version 9 (1.19.0) — Audit log offline.
    //
    // Локальный кэш событий + outbox для записи. Append-only по дизайну
    // (audit_log не редактируется и не удаляется). Стратегия id:
    // crypto.randomUUID() локально → тот же id уходит на сервер.
    // upsert(onConflict:'id') гарантирует идемпотентность при retry.
    //
    // Индексы:
    //   • [entity_type+entity_id] — главный read-pattern: история
    //     событий конкретной заявки/объекта/клиента.
    //   • company_id — RLS guard на клиенте + incremental sync filter.
    //   • server_timestamp — сортировка для рендера ленты (свежие сверху).
    //
    // Заметка про client_timestamp vs server_timestamp:
    //   • Когда событие пишется оффлайн, server_timestamp пустой —
    //     ставим client_timestamp (момент действия пилота). Сервер
    //     при INSERT заполнит server_timestamp = now() своими часами.
    //   • После успешной outbox-отправки локальный row обновляется
    //     серверным server_timestamp через incremental sync.
    //   • UI всегда сортирует по server_timestamp с fallback на
    //     client_timestamp если server ещё пустой.
    // Version 10 (1.19.8) — Offline cache реквизитов компании для PaymentScreen.
    //
    // Контекст: PaymentScreen техника при оплате через СБП/перевод
    // вызывает fetchPublicCompanyInfo с сервера. Если техник в офлайне,
    // функция возвращает null, и UI показывает "Номер телефона для СБП
    // не указан" — даже если реквизиты компании давно сконфигурированы.
    //
    // Решение: кэшируем PublicCompanyInfo в Dexie. При логине / на старте
    // фоновой задачей подтягиваем свежий снимок. PaymentScreen всегда
    // читает из Dexie, поэтому работает в любом состоянии сети.
    //
    // Структура: один rec на company_id. Никаких индексов кроме PK —
    // мы всегда знаем company_id заявки и читаем точечно.
    this.version(10).stores({
      orders: 'id, status, assigned_to, customer_id, scheduled_date, updated_at, repeat_of, [assigned_to+scheduled_date]',
      visit_sessions: 'order_id, company_id, current_screen, started_at, completed_at',
      checklist_results: 'id, order_id, item_id, status',
      photos: 'id, order_id, upload_status, taken_at',
      signatures: 'order_id, upload_status, signed_at',
      outbox: 'id, status, created_at, order_id, next_retry_at, locked_at',
      templates: 'id, work_type, is_active',
      checklist_photos: 'id, order_id, [order_id+item_id], upload_status, taken_at',
      customers: 'id, company_id, phone, name, updated_at, [company_id+phone], [company_id+name]',
      customer_objects: 'id, company_id, customer_id, is_active, updated_at, [company_id+is_active], [customer_id+is_active]',
      object_types: 'id, company_id, is_active, [company_id+is_active]',
      invoices: 'id, company_id, order_id, status, updated_at',
      sync_meta: 'key, entity, company_id',
      audit_log: 'id, company_id, entity_id, [entity_type+entity_id], server_timestamp, client_timestamp',
      // 1.19.8 — кэш реквизитов компании для офлайн-оплаты.
      company_info: 'company_id, updated_at',
    });

    // 1.27.0 — P0-3a: acquiring_settings.
    //
    // Кэш публичных полей company_acquiring_settings (без секретов).
    // Один rec на company_id. Используется PaymentScreen для решения,
    // показывать ли кнопку «Онлайн-оплата». Хранится локально потому
    // что:
    //   • Это «состояние компании», как и company_info — не имеет
    //     смысла каждый раз дёргать сеть.
    //   • Это позволяет PaymentScreen работать в офлайне корректно
    //     (мы заранее знаем, что эквайринг недоступен, и сразу
    //     прячем кнопку, не дожидаясь онлайн-ответа).
    this.version(11).stores({
      orders: 'id, status, assigned_to, customer_id, scheduled_date, updated_at, repeat_of, [assigned_to+scheduled_date]',
      visit_sessions: 'order_id, company_id, current_screen, started_at, completed_at',
      checklist_results: 'id, order_id, item_id, status',
      photos: 'id, order_id, upload_status, taken_at',
      signatures: 'order_id, upload_status, signed_at',
      outbox: 'id, status, created_at, order_id, next_retry_at, locked_at',
      templates: 'id, work_type, is_active',
      checklist_photos: 'id, order_id, [order_id+item_id], upload_status, taken_at',
      customers: 'id, company_id, phone, name, updated_at, [company_id+phone], [company_id+name]',
      customer_objects: 'id, company_id, customer_id, is_active, updated_at, [company_id+is_active], [customer_id+is_active]',
      object_types: 'id, company_id, is_active, [company_id+is_active]',
      invoices: 'id, company_id, order_id, status, updated_at',
      sync_meta: 'key, entity, company_id',
      audit_log: 'id, company_id, entity_id, [entity_type+entity_id], server_timestamp, client_timestamp',
      company_info: 'company_id, updated_at',
      // 1.27.0 — Один rec на company_id (PK). Поле status индексируется
      // потому что фильтр «active» — частая операция.
      acquiring_settings: 'company_id, status, updated_at',
    });
  }
}

export const db = new KvorumDB();

export async function seedPilotTemplates() {
  await db.templates.bulkPut(getPilotChecklistTemplates());
}

export async function clearSessionScopedData() {
  await Promise.all([
    db.orders.clear(),
    db.visit_sessions.clear(),
    db.checklist_results.clear(),
    db.photos.clear(),
    db.signatures.clear(),
    db.outbox.clear(),
    // 1.15.0 — также очищаем фото пунктов чек-листа при logout.
    db.checklist_photos.clear(),
    // 1.17.0a — бизнес-сущности.
    db.customers.clear(),
    db.customer_objects.clear(),
    db.object_types.clear(),
    db.invoices.clear(),
    // 1.18.1 — sync cursors. При logout/login будем синхронизировать
    // с нуля как при первом запуске.
    db.sync_meta.clear(),
    // 1.19.0 — audit log: чистим локальный кэш событий. История на
    // сервере сохраняется, при следующем логине incremental sync
    // подтянет последние события.
    db.audit_log.clear(),
    // 1.19.8 — реквизиты компании. Перезагрузятся при следующем логине
    // через hydrateCompanyInfo.
    db.company_info.clear(),
    // 1.27.0 — настройки эквайринга. Перезагрузятся при следующем
    // логине через hydrateAcquiringSettings (см. authStore).
    db.acquiring_settings.clear(),
  ]);
}

export async function clearVisitData(orderId: string) {
  await Promise.all([
    db.visit_sessions.delete(orderId),
    db.checklist_results.where('order_id').equals(orderId).delete(),
    db.photos.where('order_id').equals(orderId).delete(),
    db.signatures.delete(orderId),
    // 1.15.0
    db.checklist_photos.where('order_id').equals(orderId).delete(),
  ]);
}
